import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {AnalysisGraphQL, isAnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {PollingAnalysis} from 'analysis-tool/polling/PollingPipeline';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import refreshTemplateSelectorQuery from 'analysis-tool/templates/refreshTemplateSelectorQuery';
import AnalysisTemplateUIHeader from 'analysis-tool/ui/header/AnalysisTemplateUIHeader';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {fetchQueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import {WHITE} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import useSidebarDragState, {SidebarResizeSeparator} from 'ui-library/hooks/useSidebarDragState';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import {Optional} from 'util/Types';

import AnalysisRightSidebarRouter from '../right-sidebar/AnalysisRightSidebarRouter';
import AnalysisClientStateIdContext from './AnalysisClientStateIdContext';
import getDefaultMainViewItemForAnalysis from './getDefaultMainViewItemForAnalysis';
import AnalysisUIHeader from './header/AnalysisUIHeader';
import useAnalysisUISidebarState from './hooks/useAnalysisUISidebarState';
import useRightSidebarItem from './hooks/useRightSidebarItem';
import useSelectedAnalysisItem from './hooks/useSelectedAnalysisItem';
import AnalysisUIMainViewRouter from './main-view/AnalysisUIMainViewRouter';
import {
  AnalysisMainViewItem,
  isStepInitializationAnalysisItem,
  isViewStepConfigAnalysisItem,
} from './main-view/MainViewTypes';
import AnalysisUISidebar from './sidebar/AnalysisUISidebar';
import {DEFAULT_HORIZONTAL_PADDING} from './sidebar/AnalysisUISidebarConstants';

const DEFAULT_SIDEBAR_WIDTH = 300;
const MAX_SIDEBAR_WIDTH = 700;
const MIN_SIDEBAR_WIDTH = 100;

interface BaseAnalysisUISkeletonProps {
  clientStateId: string;
}

export interface PresentationalAnalysisUISkeletonProps extends BaseAnalysisUISkeletonProps {
  analysis: AnalysisGraphQL | AnalysisTemplateGraphQL;
}

export interface AnalysisUISkeletonProps extends BaseAnalysisUISkeletonProps {
  analysisId: string;
}

export function PresentationalAnalysisUISkeleton({
  clientStateId,
  analysis,
}: PresentationalAnalysisUISkeletonProps): JSX.Element {
  const sidebarState = useAnalysisUISidebarState(clientStateId);
  const selectedItemState = useSelectedAnalysisItem(clientStateId, analysis);
  const {selectedRightSidebarItem, setSelectedRightSidebarItem} = useRightSidebarItem(clientStateId);
  const {dragHandleProps, isResizing, sidebarWidth, sidebarWrapperProps} = useSidebarDragState({
    sidebarSide: 'left',
    defaultWidth: DEFAULT_SIDEBAR_WIDTH,
    minWidth: MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH,
  });
  const isReconfigurationView = isViewStepConfigAnalysisItem(selectedItemState.selectedItem);
  const isSidebarOpen =
    sidebarState.isOpen &&
    !isStepInitializationAnalysisItem(selectedItemState.selectedItem) &&
    !isReconfigurationView;

  function openStepInitializationView(newSelectedItem: AnalysisMainViewItem): void {
    selectedItemState.onChangeSelectedItem(newSelectedItem);
    sidebarState.close();
  }

  function closeStepInitializationView(newSelectedItem?: Optional<AnalysisMainViewItem>): void {
    // Reopen the sidebar after returning to the default view, to very obviously put the user back into the
    // Analysis UI experience.
    selectedItemState.onChangeSelectedItem(newSelectedItem ?? getDefaultMainViewItemForAnalysis(analysis));
    sidebarState.open();
  }

  return (
    <div
      className={classNames(css(styles.wrapper), css(isResizing && styles.sidebarResizing))}
      {...sidebarWrapperProps}
      {...getTestProps({component: 'AnalysisUISkeleton', element: 'root'})}
    >
      <TabLayout
        backgroundColor={TabLayoutBackgroundColor.GRAY_LIGHTER}
        className={css(styles.tabLayout)}
        header={
          isAnalysisGraphQL(analysis) ? (
            <AnalysisUIHeader
              analysis={analysis}
              showOpenSidebarButton={
                !isSidebarOpen &&
                !isStepInitializationAnalysisItem(selectedItemState.selectedItem) &&
                !isReconfigurationView
              }
              onOpenSidebar={sidebarState.open}
              onOpenStepInitializationView={openStepInitializationView}
              onOpenRightSidebarItem={setSelectedRightSidebarItem}
              onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
            />
          ) : (
            <AnalysisTemplateUIHeader
              analysisTemplate={analysis}
              showOpenSidebarButton={
                !isSidebarOpen &&
                !isStepInitializationAnalysisItem(selectedItemState.selectedItem) &&
                !isReconfigurationView
              }
              onOpenSidebar={sidebarState.open}
              onOpenStepInitializationView={openStepInitializationView}
              onOpenRightSidebarItem={setSelectedRightSidebarItem}
              onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
            />
          )
        }
        isHeaderFrozen={true}
        dataTestAttrs={{component: 'AnalysisUISkeleton', element: 'tabLayout'}}
      >
        <div className={css(styles.bodyWrapper)}>
          {isSidebarOpen && (
            <>
              <div
                className={classNames('scrollbar-override', css(styles.sidebarWrapper))}
                style={{
                  flex: `0 0 ${sidebarWidth}px`,
                }}
                {...getTestProps({component: 'AnalysisUISkeleton', element: 'sidebarRoot'})}
              >
                <AnalysisUISidebar
                  analysis={analysis}
                  onCloseSidebar={sidebarState.close}
                  onOpenStepInitializationView={openStepInitializationView}
                  selectedItemState={selectedItemState}
                  onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
                />
              </div>
              <SidebarResizeSeparator isResizing={isResizing} dragHandleProps={dragHandleProps} />
            </>
          )}
          <div className={css(styles.contentWrapper)}>
            <div className={classNames(css(styles.mainViewWrapper), 'scrollbar-override')}>
              <AnalysisUIMainViewRouter
                analysis={analysis}
                clientStateId={clientStateId}
                selectedItem={selectedItemState.selectedItem}
                onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
                onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
                onCloseStepInitializationView={closeStepInitializationView}
                onOpenStepInitializationView={openStepInitializationView}
              />
            </div>
            <AnalysisRightSidebarRouter
              analysis={analysis}
              clientStateId={clientStateId}
              selectedRightSidebarItem={selectedRightSidebarItem}
              onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
              onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
            />
          </div>
        </div>
      </TabLayout>
    </div>
  );
}

const MemoizedPresentationalAnalysisUISkeleton = React.memo(PresentationalAnalysisUISkeleton);

export default function QueryingAnalysisUISkeleton(props: AnalysisUISkeletonProps): JSX.Element {
  // Eagerly start loading Query API Introspection and schema dropdown data, which can take a long time to load.
  // Doing this now prevents a long loading experience when the user creates a dataframe using the Query API
  // form, which we expect to be a common path.
  useOnMount(() => {
    fetchQueryApiIntrospection();
    doQuerySafe<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY});
    if (isAnalysisTemplatesEnabled()) {
      refreshTemplateSelectorQuery('cache-first');
    }
  });
  return (
    <AnalysisClientStateIdContext.Provider value={props.clientStateId}>
      <PollingAnalysis analysisId={props.analysisId}>
        {(analysis) => <MemoizedPresentationalAnalysisUISkeleton {...props} analysis={analysis} />}
      </PollingAnalysis>
    </AnalysisClientStateIdContext.Provider>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  sidebarResizing: {
    cursor: 'col-resize',
  },
  tabLayout: {
    flex: 1,
  },
  bodyWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  sidebarWrapper: {
    backgroundColor: WHITE,
    display: 'flex',
    flexDirection: 'column',
    // Right-padding should be managed by the sidebar, since it has some elements that should be padded
    // (like the sidebar content) and some that should not be (like the collapse button)
    padding: `12px 0px 16px ${DEFAULT_HORIZONTAL_PADDING}px`,
    overflow: 'auto',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflowX: 'hidden',
  },
  mainViewWrapper: {
    // If changes are made to this style, make sure to test extensively, since different views have different needs from its parent.
    // Empty Analysis is a short view, Create outputs has multiple panes, etc.
    flex: 1,
    height: '100%',
    maxWidth: '100%',
    minWidth: 1,
  },
});
